import { AlertTriangleIcon, GitBranchAIcon, LightbulbBIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Alert, Dialog, FeaturedIcon, Stack, Text } from '@prophecy/ui-v3';
import { formatBytes } from '@prophecy/utils/fmt';
import { pluralizeWithNumber } from '@prophecy/utils/string';
import styled from 'styled-components';

import { BranchStatusBadge } from './BranchStatusBadge';
import { BranchStatus, LocalCheckout, LocalCheckoutWarning } from './types';

const BranchList = styled.div`
  background: ${theme.colors.gray50};
  border-radius: ${theme.radius.x8};
  padding: ${theme.spaces.x4} ${theme.spaces.x12};
  max-height: ${theme.width.x320};
  overflow-y: auto;
  min-height: 0;
`;

const BranchRow = styled(Stack)`
  padding: ${theme.spaces.x12} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.gray200};
  }
`;

const TipAlert = styled(Alert)`
  box-shadow: none;
  border-color: ${theme.colors.gray300};
`;

function getStorageFreedBytes(checkouts: LocalCheckout[]) {
  return checkouts.reduce((sum, checkout) => sum + (checkout.detail?.sizeOnDiskBytes ?? 0), 0);
}

type DeleteConfirmationModalProps = {
  open: boolean;
  projectName: string;
  checkouts: LocalCheckout[];
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export function DeleteConfirmationModal({
  open,
  projectName,
  checkouts,
  deleting,
  onClose,
  onConfirm
}: DeleteConfirmationModalProps) {
  const count = checkouts.length;
  const storageFreed = formatBytes(getStorageFreedBytes(checkouts));

  return (
    <Dialog
      open={open}
      size='l'
      closable={!deleting}
      onClose={onClose}
      onOk={onConfirm}
      icon={
        <FeaturedIcon variant='light' size='l' tone={BaseColor.warning} icon={<AlertTriangleIcon type='default' />} />
      }
      closeButton={{ children: 'Cancel', disabled: deleting }}
      okButton={{
        children: `Delete ${pluralizeWithNumber(count, 'branch', 'es')}`,
        danger: true,
        loading: deleting,
        disabled: deleting || count === 0
      }}
      data-test-id='delete-branches-confirmation-modal'>
      <Stack gap={theme.spaces.x20} padding={`${theme.spaces.x20} 0`}>
        <Text level='lg' weight={theme.fontWeight.semiBold}>
          Delete {pluralizeWithNumber(count, 'branch', 'es')}?
        </Text>
        <Text level='sm' tone={theme.colors.gray600}>
          This will remove the local copy of {pluralizeWithNumber(count, 'branch', 'es')} from "{projectName}", freeing
          up {storageFreed}. Nothing will change in your Git repository, and you can restore any of them later by
          checking the branch out again.
        </Text>
        <BranchList>
          {checkouts.map((checkout) => {
            const uncommittedCount =
              checkout.detail?.warning === LocalCheckoutWarning.DIRTY
                ? (checkout.detail.uncommittedFileCount ?? 0)
                : undefined;
            return (
              <BranchRow
                key={checkout.userRepositoryUid}
                direction='horizontal'
                gap={theme.spaces.x8}
                alignY='center'
                align='space-between'
                data-test-id={`delete-branch-row-${checkout.userRepositoryUid}`}>
                <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
                  <GitBranchAIcon type='default' size='xs' color={theme.colors.gray500} />
                  <Text level='sm' weight={theme.fontWeight.medium}>
                    {checkout.branch}
                  </Text>
                  <Text level='sm' tone={theme.colors.gray500}>
                    {checkout.user.name}
                  </Text>
                </Stack>
                {uncommittedCount != null ? (
                  <BranchStatusBadge status={{ type: BranchStatus.Uncommitted, fileCount: uncommittedCount }} />
                ) : null}
              </BranchRow>
            );
          })}
        </BranchList>
        <TipAlert variant='gray' showIcon={false} data-test-id='delete-branches-tip'>
          <Stack direction='horizontal' gap={theme.spaces.x12} alignY='start'>
            <LightbulbBIcon type='default' size='s' color={theme.colors.gray700} />
            <Text level='sm' tone={theme.colors.gray700}>
              Need a branch back later? Create a branch with the same name and Prophecy syncs it from Git again.
            </Text>
          </Stack>
        </TipAlert>
      </Stack>
    </Dialog>
  );
}
